/**
 * The decision layer — a utility AI over eight intents.
 *
 * ===========================================================================
 * WHY UTILITY AND NOT A STATE MACHINE
 * ===========================================================================
 *
 * Every intent is scored every tick by a pure function of the context, and the
 * highest score wins. There is no transition table, so there is no state a
 * scenario can be stuck in and no pair of states that can ping-pong. Each
 * `score*` function below can be called with a hand-built context and asserted
 * on its own, which is what makes the decision layer testable at all — a state
 * machine only fails in composition, and composition is what a bot spends its
 * whole life in.
 *
 * ===========================================================================
 * ROLES: WHY TWO TEAMMATES NEVER BOTH COMMIT
 * ===========================================================================
 *
 * The classic bot-team failure is both cars driving at the ball, colliding, and
 * leaving the net open. That is not fixed here by a penalty term — a penalty is
 * a preference and preferences can be outvoted. It is fixed STRUCTURALLY:
 *
 *   1. `assignRoles` sorts a team by predicted time-to-ball and hands out
 *      exactly one 'first', one 'second' and 'third' to everyone else. The sort
 *      is total (ties break on car index) so the assignment is deterministic.
 *   2. `goForBall`, `challenge` and `clear` return NEGATIVE_INFINITY for any
 *      role other than 'first'.
 *
 * A second man therefore cannot score a committing intent at all, whatever the
 * numbers say. `test/bot/intents.test.ts` asserts it over a sweep of positions.
 *
 * ===========================================================================
 * TIME-TO-BALL IS NOT A DISTANCE
 * ===========================================================================
 *
 * Scoring on Euclidean distance produces a bot that thinks a ball 500 uu
 * directly behind it is closer than one 800 uu straight ahead. It is not: at
 * 1000 uu/s the turn radius is 425 uu, so reversing the heading costs the best
 * part of a second.
 *
 * `estimateTimeToReach` therefore builds a path from the REAL turn model —
 * `steerCurvature` from ../physics/ground, the same curve the car actually
 * drives — and then integrates the REAL acceleration curve, `throttleAccel`,
 * plus boost if the tier is allowed to spend it. Two approximations remain and
 * are deliberate: the path is a single turn followed by a straight (a Dubins
 * path with one arc), and reversing is not considered. Both make the estimate
 * pessimistic for targets behind the car, which is the safe direction — a bot
 * that under-claims a ball rotates instead of colliding with its teammate.
 *
 * Interception, not pursuit: `estimateBallReach` walks the predicted trajectory
 * and takes the first sample the car can reach BY THE TIME the ball is there.
 */

import { GOAL, GOAL_CENTER, BOOST_PADS } from '../physics/arena-data';
import type { CarState } from '../physics/car-state';
import {
  ARENA,
  BALL_RADIUS,
  BOOST_ACCELERATION,
  BOOST_CONSUMPTION_RATE,
  BOOST_MAX,
  CAR_MAX_SPEED,
  CAR_MAX_THROTTLE_SPEED,
  DT,
} from '../physics/constants';
import { steerCurvature, throttleAccel } from '../physics/ground';
import { clamp, clone, dist, dot, forward, length, sub, vec3, type Vec3 } from '../physics/math';
import type { DifficultyParams } from './difficulty';
import { TAKEOFF_DOUBLE, TAKEOFF_SINGLE, verticalReachFor } from './mechanics';
import { makeSample, type BallPrediction, type BallSample } from './prediction';

// ---------------------------------------------------------------------------
// Intents and roles
// ---------------------------------------------------------------------------

export type Intent =
  /** Drive at the predicted interception and hit the ball toward their goal. */
  | 'goForBall'
  /** Meet an opponent's touch early rather than let them hit it at our net. */
  | 'challenge'
  /** The ball is predicted into our own goal and we can get there first. */
  | 'save'
  /** The ball is loose in our defensive third; get it out. */
  | 'clear'
  /** Stay goalside of the ball, matching its lateral position. */
  | 'shadowDefend'
  /** Rotate to the far post and wait. */
  | 'rotateBackPost'
  /** Take a pad. */
  | 'collectBoost'
  /** Run an opponent down while supersonic. */
  | 'demo';

/**
 * Declaration order is the tie-break order: `chooseIntent` uses a strict `>`,
 * so an exact tie keeps whichever appears first here. Ordered most to least
 * committal so a tie resolves toward action.
 */
export const INTENTS: readonly Intent[] = [
  'save',
  'clear',
  'goForBall',
  'challenge',
  'demo',
  'shadowDefend',
  'rotateBackPost',
  'collectBoost',
];

/** Position in the team's rotation, assigned every tick by `assignRoles`. */
export type Role = 'first' | 'second' | 'third';

/** Score of an intent this car is not permitted to hold. */
export const INTENT_UNAVAILABLE = Number.NEGATIVE_INFINITY;

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

/** Centre of the goal `team` defends. Blue (0) defends -Y. */
export const ownGoalCenter = (team: 0 | 1): Vec3 => clone(GOAL_CENTER[team]);

/** Centre of the goal `team` attacks. */
export const attackingGoalCenter = (team: 0 | 1): Vec3 => clone(GOAL_CENTER[team === 0 ? 1 : 0]);

/** `y` re-expressed so that larger is closer to `team`'s OWN goal. */
export const towardOwnGoal = (team: 0 | 1, y: number): number => (team === 0 ? -y : y);

/** 0 on the opponent's goal line, 0.5 at halfway, 1 on our own goal line. */
export const defensiveness = (team: 0 | 1, y: number): number =>
  clamp((towardOwnGoal(team, y) + ARENA.halfLength) / (2 * ARENA.halfLength), 0, 1);

/**
 * Ball centre above which the bot has to LEAVE THE GROUND.
 *
 * Deliberately a mechanic boundary and not a physical one, and the difference
 * matters. A full-hold jump physically reaches a ball centred at
 * `JUMP_REACH_MAX_Z` below — 354 uu — but `groundShot` in ./mechanics never
 * presses jump except to flip, so anything the ground mechanics are handed
 * above about this height is a ball the car drives underneath. 250 uu is the
 * height at which that stops being a shot and starts being a miss; above it the
 * decision layer routes to an aerial instead. Raising it to the true jump reach
 * without teaching `groundShot` to jump would hand it 100 uu of balls it cannot
 * touch, which is the failure this constant exists to prevent.
 */
export const GROUND_REACH_MAX_Z = 250;

/**
 * Highest ball CENTRE a car can touch from a standing start, straight up.
 *
 * Both are the takeoff profiles in ./mechanics — integrated at the simulation's
 * own tick, sticky force and all — plus the distance from the car's origin to
 * the centre of a ball on its roof. They come out at 354 uu for a full-hold
 * jump and 606 uu for the same jump with the second one spent as early as the
 * button edge allows, against the 148 uu a car reaches with its wheels down.
 *
 * The second jump is therefore worth 252 uu of reach, and it costs no boost at
 * all: these are the heights an EMPTY car can contest.
 */
export const JUMP_REACH_MAX_Z = TAKEOFF_SINGLE.apex + verticalReachFor();
export const DOUBLE_JUMP_REACH_MAX_Z = TAKEOFF_DOUBLE.apex + verticalReachFor();

// ---------------------------------------------------------------------------
// The drive model
// ---------------------------------------------------------------------------

/**
 * Length of the path the car must actually drive to `target`: one arc at the
 * current speed's minimum turn radius, then a straight.
 *
 * `arc` is what the turn costs; `chord` is the displacement that arc buys. When
 * the target is further away than the chord, the remainder is driven straight.
 * When it is nearer — the target is inside the turn circle, the case a
 * Euclidean estimate gets most wrong — the difference is added instead, because
 * the car has to come back around.
 */
export const drivePathLength = (car: CarState, target: Vec3): number => {
  const to = sub(target, car.position);
  to.z = 0;
  const straight = length(to);
  if (straight < 1e-6) return 0;

  const fwd = forward(car.orientation);
  const fx = fwd.x;
  const fy = fwd.y;
  const fLen = Math.hypot(fx, fy);
  if (fLen < 1e-6) return straight;

  const cosA = clamp((fx * to.x + fy * to.y) / (fLen * straight), -1, 1);
  const angle = Math.acos(cosA);

  const radius = 1 / steerCurvature(length(car.velocity));
  const arc = radius * angle;
  const chord = 2 * radius * Math.sin(angle / 2);
  return arc + Math.abs(straight - chord);
};

/**
 * ---------------------------------------------------------------------------
 * The acceleration model, precomputed
 * ---------------------------------------------------------------------------
 *
 * `estimateBallReach` asks "how long to cover this distance?" for every fourth
 * tick of a 720-tick trajectory, for every car, every tick. Integrating the
 * throttle curve forward on each of those calls is roughly 6000 sub-steps per
 * car per tick, which is a millisecond of budget spent on an ESTIMATE — so the
 * integration is done ONCE, at module load, and every query is two binary
 * searches into the result.
 *
 * Nothing is approximated by doing this. The tables are the real curve —
 * `throttleAccel` and `BOOST_ACCELERATION`, integrated at the real 120 Hz tick
 * with the same explicit-Euler bias the game has — sampled from rest. Because
 * the speed trajectory from rest is a fixed function of time, a car already
 * moving at v0 is simply further along the same curve: find its time on the
 * curve, add the distance, read the time back out.
 *
 * The one genuine approximation is the boost tank. Two curves are built,
 * throttle-only and throttle+boost-forever, and a car picks the boosted one
 * only when it holds enough boost to sprint for `BOOST_SPRINT_SECONDS`. A car
 * with half a tank is therefore estimated as slightly slow. That direction is
 * safe: it under-claims balls, which costs a rotation rather than a collision.
 */

/** Boost held (in units) below which a car is estimated on the throttle curve. */
const BOOST_SPRINT_SECONDS = 1.2;
const BOOST_SPRINT_MIN = BOOST_CONSUMPTION_RATE * BOOST_SPRINT_SECONDS;

interface SpeedCurve {
  /** Speed at each sample, monotonically increasing. */
  readonly speed: Float64Array;
  /** Cumulative distance at each sample, strictly increasing after the first. */
  readonly distance: Float64Array;
  /** Speed the curve ends at; beyond the table, motion is at this speed. */
  readonly terminal: number;
}

const buildSpeedCurve = (useBoost: boolean, steps: number): SpeedCurve => {
  const speed = new Float64Array(steps);
  const distance = new Float64Array(steps);
  const cap = useBoost ? CAR_MAX_SPEED : CAR_MAX_THROTTLE_SPEED;

  let v = 0;
  let s = 0;
  for (let i = 0; i < steps; i++) {
    speed[i] = v;
    distance[i] = s;
    const accel = throttleAccel(v) + (useBoost ? BOOST_ACCELERATION : 0);
    v = Math.min(v + accel * DT, cap);
    s += v * DT;
  }
  return { speed, distance, terminal: speed[steps - 1] };
};

/** 4 s reaches 1400 uu/s (the curve is asymptotic past 2.24 s). */
const THROTTLE_CURVE = buildSpeedCurve(false, 480);
/** 2.5 s reaches the 2300 uu/s cap (1.70 s per docs/FEEL_TARGETS.md). */
const BOOST_CURVE = buildSpeedCurve(true, 300);

/** Fractional index of the first sample at or above `value`, in an ascending
 *  table. Returns `table.length - 1` when `value` is past the end. */
const lookup = (table: Float64Array, value: number): number => {
  const n = table.length;
  if (value <= table[0]) return 0;
  if (value >= table[n - 1]) return n - 1;

  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (table[mid] <= value) lo = mid;
    else hi = mid;
  }
  const span = table[hi] - table[lo];
  return span > 0 ? lo + (value - table[lo]) / span : lo;
};

const sampleAt = (table: Float64Array, index: number): number => {
  const lo = Math.floor(index);
  if (lo >= table.length - 1) return table[table.length - 1];
  return table[lo] + (table[lo + 1] - table[lo]) * (index - lo);
};

/**
 * Seconds to cover `distance` starting at `speed`, on the real drive curve.
 * `boostAmount` is the boost the caller is willing to spend, not the tank.
 */
export const timeToCoverDistance = (
  distance: number,
  speed: number,
  boostAmount: number,
  useBoost: boolean
): number => {
  if (distance <= 0) return 0;

  const curve = useBoost && boostAmount >= BOOST_SPRINT_MIN ? BOOST_CURVE : THROTTLE_CURVE;
  const v0 = clamp(speed, 0, curve.terminal);

  const startIndex = lookup(curve.speed, v0);
  const startDistance = sampleAt(curve.distance, startIndex);
  const wanted = startDistance + distance;

  const last = curve.distance.length - 1;
  if (wanted >= curve.distance[last]) {
    // Past the table: the car is at terminal speed and the rest is linear.
    const tail = (wanted - curve.distance[last]) / curve.terminal;
    return (last - startIndex) * DT + tail;
  }
  return (lookup(curve.distance, wanted) - startIndex) * DT;
};

export interface ReachOptions {
  /** Subtract this from the path — the car only has to get within contact
   *  range of the target, not stand on it. */
  arriveOffset?: number;
  /** Override the tier's boost policy (a save is worth the whole tank). */
  useBoost?: boolean;
}

/** Seconds for `car` to reach `target`, through the real turn and drive model. */
export const estimateTimeToReach = (
  car: CarState,
  target: Vec3,
  params: DifficultyParams,
  options: ReachOptions = {}
): number => {
  const path = Math.max(0, drivePathLength(car, target) - (options.arriveOffset ?? 0));
  const speed = Math.max(0, dot(car.velocity, forward(car.orientation)));
  const useBoost = options.useBoost ?? params.useBoostForSpeed;
  const budget = useBoost ? Math.max(0, car.boostAmount - params.boostReserve) : 0;
  return timeToCoverDistance(path, speed, budget, useBoost);
};

/** Where and when this car can first meet the ball. */
export interface BallReach {
  /** True when an interception was found inside the prediction horizon. */
  reachable: boolean;
  /** Seconds from now. */
  time: number;
  /** Prediction tick of the interception. */
  tick: number;
  position: Vec3;
}

/** Contact offset: the car meets the ball's surface, not its centre. */
const CONTACT_OFFSET = BALL_RADIUS;

/**
 * Earliest interception on the predicted trajectory.
 *
 * When nothing on the trajectory can be reached (the ball is running away, or
 * it is in the air and this tier has no aerial) the last sample is returned
 * with `reachable: false` and the honest chase time, so callers still have a
 * point to drive at while knowing the race is lost.
 */
export const estimateBallReach = (
  car: CarState,
  prediction: BallPrediction,
  params: DifficultyParams,
  out: BallSample = makeSample()
): BallReach => {
  // How high the ball may be and still be worth chasing. An aerial tier is not
  // capped at all — with boost the ceiling is the ceiling — so the only tiers
  // this bounds are the ones that have to get there on jumps alone, and for
  // those the bound is now the REAL reach rather than the ground mechanics'.
  const maxZ = params.mechanics.aerials
    ? ARENA.ceiling
    : params.mechanics.doubleJump
      ? DOUBLE_JUMP_REACH_MAX_Z
      : GROUND_REACH_MAX_Z;
  const horizon = Math.min(prediction.ticks, params.predictionHorizonTicks);

  const hit = prediction.positionWhenReachable(
    (position, time) =>
      position.z <= maxZ &&
      estimateTimeToReach(car, position, params, { arriveOffset: CONTACT_OFFSET }) <= time,
    { stepTicks: 4, maxTick: horizon, out }
  );

  if (hit !== null) {
    return { reachable: true, time: hit.time, tick: hit.tick, position: clone(hit.position) };
  }

  const last = prediction.readInto(horizon - 1, out);
  return {
    reachable: false,
    time: estimateTimeToReach(car, last.position, params, { arriveOffset: CONTACT_OFFSET }),
    tick: last.tick,
    position: clone(last.position),
  };
};

/**
 * Seconds until the ball crosses `team`'s own goal line if nobody touches it,
 * or Infinity. Uses the same threshold the world scores on, so a "save" is
 * exactly the set of balls that would otherwise be a goal.
 */
export const goalThreat = (prediction: BallPrediction, team: 0 | 1): number => {
  const sample = makeSample();
  for (let t = 0; t < prediction.ticks; t += 2) {
    prediction.readInto(t, sample);
    if (towardOwnGoal(team, sample.position.y) > GOAL.scoreBaseThresholdY) return sample.time;
  }
  return Number.POSITIVE_INFINITY;
};

// ---------------------------------------------------------------------------
// Boost pads
// ---------------------------------------------------------------------------

export interface BoostTarget {
  /** Index into BOOST_PADS / RLBot FieldInfo order. */
  index: number;
  position: Vec3;
  isBig: boolean;
  /** Extra path length versus going straight to `via`, in uu. */
  detour: number;
}

/**
 * A big pad refills the tank outright, so it is worth a real detour. 900 uu is
 * about 0.8 s at cruising speed — the point at which taking 100 boost stops
 * paying for itself.
 */
const BIG_PAD_DETOUR_BONUS = 900;

/**
 * The pad worth taking on the way to `via`, or null when none is collectable.
 * Scored on DETOUR rather than distance: a pad 1500 uu away that is directly on
 * the route is free, and one 400 uu away in the wrong direction is not.
 */
export const nearestBoostTarget = (
  car: CarState,
  padActive: (index: number) => boolean,
  via: Vec3
): BoostTarget | null => {
  let best: BoostTarget | null = null;
  let bestCost = Number.POSITIVE_INFINITY;
  const direct = dist(car.position, via);

  for (let i = 0; i < BOOST_PADS.length; i++) {
    if (!padActive(i)) continue;
    const pad = BOOST_PADS[i];
    const detour = dist(car.position, pad.position) + dist(pad.position, via) - direct;
    const cost = detour - (pad.isBig ? BIG_PAD_DETOUR_BONUS : 0);
    if (cost < bestCost) {
      bestCost = cost;
      best = { index: i, position: clone(pad.position), isBig: pad.isBig, detour };
    }
  }
  return best;
};

// ---------------------------------------------------------------------------
// Role assignment
// ---------------------------------------------------------------------------

export interface RoleCandidate {
  carIndex: number;
  /** Predicted seconds to the ball. */
  reachTime: number;
  demolished: boolean;
}

/**
 * One 'first', one 'second', 'third' for everyone else, keyed by car index.
 *
 * The ordering is (alive before demolished, then reach time, then car index).
 * The final tie-break on car index is what makes the assignment deterministic
 * when two cars are equidistant — which happens exactly at kickoff, on every
 * kickoff, so it is not a corner case.
 */
export const assignRoles = (candidates: readonly RoleCandidate[]): Map<number, Role> => {
  const order = candidates.slice().sort((a, b) => {
    if (a.demolished !== b.demolished) return a.demolished ? 1 : -1;
    if (a.reachTime !== b.reachTime) return a.reachTime - b.reachTime;
    return a.carIndex - b.carIndex;
  });

  const roles = new Map<number, Role>();
  for (let i = 0; i < order.length; i++) {
    roles.set(order[i].carIndex, i === 0 ? 'first' : i === 1 ? 'second' : 'third');
  }
  return roles;
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

/**
 * Everything the scoring functions may look at. Assembled once per bot per tick
 * by ./bot; the score functions themselves touch nothing else, which is what
 * makes them unit-testable from a literal.
 */
export interface IntentContext {
  /** Position of every car, for intents that target one. Indexed as the world
   *  indexes its cars, so `nearestOpponent.carIndex` selects into it. */
  carPositions?: readonly Vec3[];
  tick: number;
  carIndex: number;
  car: CarState;
  team: 0 | 1;
  role: Role;
  params: DifficultyParams;

  /** This car's earliest interception. */
  reach: BallReach;
  /** Best opponent interception time, or Infinity when there are none. */
  opponentReachTime: number;
  /** Best teammate interception time excluding this car, or Infinity. */
  teammateReachTime: number;
  /** Seconds until the ball crosses our goal line untouched, or Infinity. */
  ownGoalThreat: number;

  boostTarget: BoostTarget | null;
  nearestOpponent: { carIndex: number; distance: number } | null;

  ownGoal: Vec3;
  attackingGoal: Vec3;
}

// ---------------------------------------------------------------------------
// Scoring — one pure function per intent
// ---------------------------------------------------------------------------

/** Boost held, 0..1. */
const boostFraction = (car: CarState): number => clamp(car.boostAmount / BOOST_MAX, 0, 1);

/** +1 when we beat every opponent to the ball by a second or more, -1 when we
 *  lose it by that much. The 1.5 s scale is roughly one challenge window. */
const raceAdvantage = (ctx: IntentContext): number =>
  clamp((ctx.opponentReachTime - ctx.reach.time) / 1.5, -1, 1);

// ---------------------------------------------------------------------------
// Committing from a long way out, and the deadlock that gating it produced
// ---------------------------------------------------------------------------

/**
 * How far past `maxCommitDistance` the interception is, as a fraction of that
 * distance and capped at one.
 *
 * A PENALTY, NOT A GATE, and that distinction is the bug this replaced.
 * Committing from the far side of the field is a bad idea; it is not an
 * impossibility, and expressing it as NEGATIVE_INFINITY made it one. The result
 * was a loop with no exit: shadowing parks the car away from the ball, being
 * away from the ball is what made every committing intent unavailable, and no
 * amount of evidence can outvote negative infinity. The first man is BY
 * CONSTRUCTION the best-placed car on its team — `assignRoles` sorts on reach
 * time — so a first man that CANNOT commit means nobody on that team ever does.
 */
const overCommit = (ctx: IntentContext): number =>
  clamp(
    (dist(ctx.car.position, ctx.reach.position) - ctx.params.maxCommitDistance) /
      Math.max(1, ctx.params.maxCommitDistance),
    0,
    1
  );

/** Weight of `overCommit`. Enough to lose a marginal race, not enough to make
 *  distance disqualifying on its own — see the note above. */
const OVER_COMMIT_PENALTY = 0.35;

/**
 * A first man with no live teammate is the entire team.
 *
 * `teammateReachTime` is Infinity exactly when this car has no live teammate, so
 * this is every tick of a 1v1 and the tail of any match where the rest of the
 * team has been demolished. Rotating is a manoeuvre that only means something
 * when there is somebody to rotate TO; with nobody there, holding a shadow is
 * not defence, it is never touching the ball again.
 *
 * It is a term on BOTH sides — committing gains it, waiting loses it — because a
 * bonus on one side alone can be outvoted by the rest of the numbers, and a gap
 * of twice this cannot be closed by distance. That is what makes "the only car
 * on a team is never permanently unable to pursue the ball" a property of the
 * scoring rather than a hope about it.
 */
const LAST_MAN_COMMIT = 0.2;

const lastManStanding = (ctx: IntentContext): boolean =>
  ctx.role === 'first' && ctx.teammateReachTime === Number.POSITIVE_INFINITY;

/**
 * `reach.reachable` STAYS a gate, and the distinction from `maxCommitDistance`
 * is worth stating because it looks like the same kind of lockout and is not.
 *
 * It is not self-reinforcing. `intentTarget` puts the shadow 35 % of the way
 * from the interception to our own goal, so a car that has arrived at its shadow
 * is 35 % of the ball-to-own-goal distance from the ball — at most ~3900 uu
 * anywhere in this arena — and a car that close reaches the ball inside every
 * tier's horizon. Shadowing therefore CLOSES the distance that reachability
 * depends on rather than preserving it, and the loop that `maxCommitDistance`
 * created cannot form here. `test/bot/intents.test.ts` asserts the bound.
 *
 * The one thing that did make it stick was a car that could not move at all, so
 * that nothing on the trajectory was reachable and the shadow was never
 * actually driven to. That is the braking DEVIATION documented in
 * ../physics/ground.ts, not a decision-layer problem, and the bot recovers from
 * it through `unstick` in ./mechanics rather than through this gate.
 */
export const scoreGoForBall = (ctx: IntentContext): number => {
  if (ctx.role !== 'first') return INTENT_UNAVAILABLE;
  if (!ctx.reach.reachable) return INTENT_UNAVAILABLE;

  return (
    0.55 +
    0.3 * raceAdvantage(ctx) +
    0.2 * (1 - defensiveness(ctx.team, ctx.reach.position.y)) -
    0.2 * clamp(ctx.reach.time / 3, 0, 1) +
    0.1 * boostFraction(ctx.car) -
    OVER_COMMIT_PENALTY * overCommit(ctx) +
    (lastManStanding(ctx) ? LAST_MAN_COMMIT : 0)
  );
};

/**
 * Challenging is the losing-the-race branch: an opponent will touch it first,
 * so meet them at the touch instead of backing off and being shot at. Only
 * worth it near our own half — challenging on their goal line is just a foul
 * waiting to happen.
 */
export const scoreChallenge = (ctx: IntentContext): number => {
  if (ctx.role !== 'first') return INTENT_UNAVAILABLE;
  if (!ctx.reach.reachable) return INTENT_UNAVAILABLE;
  if (ctx.opponentReachTime === Number.POSITIVE_INFINITY) return INTENT_UNAVAILABLE;

  const losing = clamp((ctx.reach.time - ctx.opponentReachTime) / 1.0, 0, 1);
  return (
    0.45 +
    0.35 * defensiveness(ctx.team, ctx.reach.position.y) +
    0.25 * losing -
    0.2 * clamp(ctx.reach.time / 3, 0, 1) +
    (lastManStanding(ctx) ? LAST_MAN_COMMIT : 0)
  );
};

/**
 * The only intent that can outscore everything else, because conceding is the
 * only outcome that cannot be recovered later in the play. Unavailable when
 * there is no threat, or when the car simply cannot arrive before the ball
 * crosses — a "save" the car cannot make is a rotation it should have made.
 */
export const scoreSave = (ctx: IntentContext): number => {
  if (ctx.ownGoalThreat === Number.POSITIVE_INFINITY) return INTENT_UNAVAILABLE;
  if (ctx.role === 'third') return INTENT_UNAVAILABLE;
  if (ctx.reach.time > ctx.ownGoalThreat) return INTENT_UNAVAILABLE;

  return (
    1.15 - 0.25 * clamp(ctx.ownGoalThreat / 4, 0, 1) + (ctx.role === 'first' ? 0.15 : 0)
  );
};

/** A loose ball in our defensive third: get it out even without a shot on. */
export const scoreClear = (ctx: IntentContext): number => {
  if (ctx.role !== 'first') return INTENT_UNAVAILABLE;
  if (!ctx.reach.reachable) return INTENT_UNAVAILABLE;

  const d = defensiveness(ctx.team, ctx.reach.position.y);
  if (d < 0.65) return INTENT_UNAVAILABLE;

  return (
    0.5 +
    0.5 * d +
    0.2 * raceAdvantage(ctx) +
    (lastManStanding(ctx) ? LAST_MAN_COMMIT : 0)
  );
};

/**
 * Always available, which is what guarantees `chooseIntent` always has a finite
 * option. Heavily penalised for the first man, who should be committing, and
 * penalised again for a first man with nobody behind him, for whom shadowing is
 * not a rotation at all — see `LAST_MAN_COMMIT`.
 */
export const scoreShadowDefend = (ctx: IntentContext): number =>
  0.42 +
  0.28 * defensiveness(ctx.team, ctx.reach.position.y) +
  0.12 * (ctx.opponentReachTime < ctx.reach.time ? 1 : 0) -
  (ctx.role === 'first' ? 0.35 : 0) -
  (ctx.role === 'third' ? 0.15 : 0) -
  (lastManStanding(ctx) ? LAST_MAN_COMMIT : 0);

export const scoreRotateBackPost = (ctx: IntentContext): number => {
  if (ctx.role === 'first') return INTENT_UNAVAILABLE;
  return (
    0.4 +
    0.25 * (ctx.role === 'third' ? 1 : 0) +
    0.2 * defensiveness(ctx.team, ctx.reach.position.y) -
    0.15 * (ctx.role === 'second' ? 1 : 0)
  );
};

/**
 * Boost hunger against urgency. The urgency term is what stops the classic
 * failure of a bot collecting a pad while the ball rolls into its own net.
 */
export const scoreCollectBoost = (ctx: IntentContext): number => {
  if (ctx.boostTarget === null) return INTENT_UNAVAILABLE;
  if (ctx.car.boostAmount >= BOOST_MAX) return INTENT_UNAVAILABLE;

  const threshold = Math.max(1, ctx.params.boostHungerThreshold);
  const need = clamp((threshold - ctx.car.boostAmount) / threshold, 0, 1);

  const ballUrgency =
    (ctx.role === 'first' ? 1 : 0.3) * (1 - clamp(ctx.reach.time / 2.5, 0, 1));
  const threatUrgency = ctx.ownGoalThreat === Number.POSITIVE_INFINITY ? 0 : 1;

  // A teammate closer to the ball than we are is cover: this is the moment a
  // pad is free rather than abandoned.
  const covered = ctx.teammateReachTime < ctx.reach.time ? 0.15 : 0;

  return (
    0.15 +
    0.95 * need +
    covered -
    0.35 * clamp(ctx.boostTarget.detour / 3000, 0, 1) -
    0.45 * ballUrgency -
    0.6 * threatUrgency
  );
};

/** Deliberately capped below the defensive intents: a demo is a bonus, never a
 *  reason to leave the net. Requires enough boost to actually reach supersonic. */
export const scoreDemo = (ctx: IntentContext): number => {
  if (!ctx.params.mechanics.demos) return INTENT_UNAVAILABLE;
  if (ctx.role === 'first') return INTENT_UNAVAILABLE;
  if (ctx.nearestOpponent === null) return INTENT_UNAVAILABLE;
  if (ctx.car.boostAmount < 50) return INTENT_UNAVAILABLE;
  if (ctx.ownGoalThreat !== Number.POSITIVE_INFINITY) return INTENT_UNAVAILABLE;

  return 0.35 + 0.2 * clamp(1 - ctx.nearestOpponent.distance / 2500, 0, 1);
};

/** Dispatch. Kept as a switch rather than a table so a new intent cannot be
 *  added without the compiler demanding a score for it. */
export const scoreIntent = (intent: Intent, ctx: IntentContext): number => {
  switch (intent) {
    case 'goForBall':
      return scoreGoForBall(ctx);
    case 'challenge':
      return scoreChallenge(ctx);
    case 'save':
      return scoreSave(ctx);
    case 'clear':
      return scoreClear(ctx);
    case 'shadowDefend':
      return scoreShadowDefend(ctx);
    case 'rotateBackPost':
      return scoreRotateBackPost(ctx);
    case 'collectBoost':
      return scoreCollectBoost(ctx);
    case 'demo':
      return scoreDemo(ctx);
  }
};

/** Every score at once. For tests and debug overlays; the hot path avoids it. */
export const scoreAll = (ctx: IntentContext): Record<Intent, number> => {
  const out = {} as Record<Intent, number>;
  for (const intent of INTENTS) out[intent] = scoreIntent(intent, ctx);
  return out;
};

// ---------------------------------------------------------------------------
// Choice, with hysteresis
// ---------------------------------------------------------------------------

export interface IntentDecision {
  intent: Intent;
  score: number;
  /** Tick the current intent was adopted. */
  sinceTick: number;
}

/**
 * Pick an intent, resisting change.
 *
 * Two independent brakes, because they stop different failures:
 *
 *   - A MINIMUM DWELL (`params.minIntentTicks`). Two intents whose scores cross
 *     back and forth every tick — which is what a car driving along a scoring
 *     boundary produces — cannot flip more often than this. Without it the bot
 *     twitches even though the margin below is satisfied on average.
 *   - A MARGIN (`params.hysteresisMargin`). A challenger must beat the
 *     incumbent by a real amount, not by a rounding error.
 *
 * The one thing that bypasses both is the incumbent becoming UNAVAILABLE: a
 * second man that has just been promoted to first must not keep shadowing, and
 * a save that is no longer a save must be dropped immediately.
 */
export const chooseIntent = (
  ctx: IntentContext,
  previous: IntentDecision | null
): IntentDecision => {
  let best: Intent = INTENTS[0];
  let bestScore = INTENT_UNAVAILABLE;
  for (const intent of INTENTS) {
    const score = scoreIntent(intent, ctx);
    if (score > bestScore) {
      bestScore = score;
      best = intent;
    }
  }

  if (previous === null) return { intent: best, score: bestScore, sinceTick: ctx.tick };

  const heldScore = scoreIntent(previous.intent, ctx);
  if (heldScore === INTENT_UNAVAILABLE) {
    return { intent: best, score: bestScore, sinceTick: ctx.tick };
  }

  const dwell = ctx.tick - previous.sinceTick;
  const keep = dwell < ctx.params.minIntentTicks || bestScore < heldScore + ctx.params.hysteresisMargin;
  if (keep) return { intent: previous.intent, score: heldScore, sinceTick: previous.sinceTick };

  return { intent: best, score: bestScore, sinceTick: ctx.tick };
};

// ---------------------------------------------------------------------------
// Intent -> a point to drive at
// ---------------------------------------------------------------------------

/**
 * The positional goal of an intent, as a world point. Shot aiming and the
 * actual controls belong to ./mechanics; this is only "where".
 *
 * Shadowing and back-post rotation are expressed relative to the ball and our
 * own goal so they stay correct on either side of the field without a mirror
 * table — the source of a whole family of team-dependent bot bugs.
 */
export const intentTarget = (intent: Intent, ctx: IntentContext): Vec3 => {
  switch (intent) {
    case 'goForBall':
    case 'challenge':
    case 'save':
    case 'clear':
      return clone(ctx.reach.position);

    case 'collectBoost':
      return ctx.boostTarget === null ? clone(ctx.reach.position) : clone(ctx.boostTarget.position);

    case 'demo': {
      const victim =
        ctx.nearestOpponent === null || ctx.carPositions === undefined
          ? undefined
          : ctx.carPositions[ctx.nearestOpponent.carIndex];
      return victim === undefined ? clone(ctx.reach.position) : clone(victim);
    }

    case 'shadowDefend': {
      // 35 % of the way from the interception to our goal, which is close enough
      // to challenge and far enough to recover. Expressed against
      // `ctx.reach.position` and not against a fixed point, so it TRACKS: an
      // unreachable ball still yields the last sample of the prediction, which
      // moves with the ball, so the shadow moves with it too.
      const t = 0.35;
      return vec3(
        ctx.reach.position.x + (ctx.ownGoal.x - ctx.reach.position.x) * t,
        ctx.reach.position.y + (ctx.ownGoal.y - ctx.reach.position.y) * t,
        0
      );
    }

    case 'rotateBackPost': {
      // The post AWAY from the ball, 400 uu off the goal line so the car is not
      // parked in its own net.
      const side = ctx.reach.position.x >= 0 ? -1 : 1;
      const post = GOAL.centerToPost * 0.8 * side;
      const inward = ctx.team === 0 ? 400 : -400;
      return vec3(post, ctx.ownGoal.y + inward, 0);
    }
  }
};

/** Ticks per second, for callers converting a decision horizon into ticks. */
export const TICKS_PER_SECOND = Math.round(1 / DT);
